import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { HandCoins } from 'lucide-react'
import { env } from '@/lib/env'
import { formatDateTimeCompact } from '@/lib/datetime'
import { fetchAllPages, type ExportColumn } from '@/lib/export'
import { formatMinorCurrency, humanizeEnum } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import {
  PENALTY_SOURCES,
  PENALTY_STATUSES,
  type CustomerPenalty,
  type PenaltyFilters,
} from '@/types/api'
import { useTableState } from '@/hooks/useTableState'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { DataTable } from '@/components/ui/DataTable'
import { ExportMenu } from '@/components/ui/ExportMenu'
import { FilterBar, FilterSelect } from '@/components/ui/FilterBar'
import { Pagination } from '@/components/ui/Pagination'
import { RowActions, StackedCell } from '@/components/ui/RowActions'
import { SearchInput } from '@/components/ui/SearchInput'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { fetchPenalties } from './api'
import { describeBand, describeStatus, describeWindow } from './describe'
import { PenaltyDetailsDrawer } from './PenaltyDetailsDrawer'
import { WaivePenaltyDialog } from './WaivePenaltyDialog'

const DEFAULT_FILTERS = {
  search: '',
  status: '',
  source: '',
}

/**
 * The cancellation penalty ledger.
 *
 * A cash booking cancelled too late cannot be refunded — no money was ever
 * captured — so what the policy retains becomes a debt, collected on the
 * customer's next booking. This screen is where an operator answers "why am I
 * being charged 250?", which is why every row carries the booking it came from,
 * the band that produced it and how long before pickup the customer cancelled,
 * rather than just an amount.
 *
 * There is no edit and no delete. A penalty leaves the balance by being settled
 * — which happens when the customer pays for their next trip — or waived, which
 * is recorded here with the administrator's name and reason.
 */
export function PenaltiesPage() {
  const table = useTableState({ defaultSort: 'createdAt', defaultFilters: DEFAULT_FILTERS })
  const [selected, setSelected] = useState<CustomerPenalty | null>(null)
  const [waiving, setWaiving] = useState<CustomerPenalty | null>(null)

  useEffect(() => {
    document.title = `Cancellation penalties · ${env.appName} Operations`
  }, [])

  const filters = useMemo<PenaltyFilters>(
    () => ({
      page: table.page,
      size: table.size,
      sort: table.sort,
      direction: table.direction,
      search: table.filters.search || undefined,
      status: table.filters.status || undefined,
      source: table.filters.source || undefined,
    }),
    [table.page, table.size, table.sort, table.direction, table.filters],
  )

  const penaltiesQuery = useQuery({
    queryKey: queryKeys.penalties.list(filters),
    queryFn: () => fetchPenalties(filters),
    placeholderData: (previous) => previous,
  })

  const columns = useMemo<ColumnDef<CustomerPenalty, unknown>[]>(
    () => [
      {
        id: 'customer',
        header: 'Customer',
        meta: { sortKey: 'userId', mobileLabel: 'Customer' },
        cell: ({ row }) => (
          <StackedCell
            primary={row.original.customerName ?? `#${row.original.userId}`}
            secondary={row.original.customerPhone ?? undefined}
          />
        ),
      },
      {
        id: 'amountMinor',
        header: 'Amount',
        meta: { sortKey: 'amountMinor', mobileLabel: 'Amount', align: 'right' },
        cell: ({ row }) => (
          <StackedCell
            primary={formatMinorCurrency(row.original.amountMinor, row.original.currency)}
            secondary={`of ${formatMinorCurrency(row.original.bookingPriceMinor, row.original.currency)}`}
          />
        ),
      },
      {
        id: 'bookingReference',
        header: 'From booking',
        meta: { sortKey: 'bookingReference', mobileLabel: 'From booking' },
        cell: ({ row }) => (
          <StackedCell
            primary={
              <span style={{ fontFamily: 'var(--font-mono)' }}>
                {row.original.sourceBookingReference ?? `#${row.original.sourceTripRequestId}`}
              </span>
            }
            secondary={formatDateTimeCompact(row.original.createdAt)}
          />
        ),
      },
      {
        id: 'policyBand',
        header: 'Band',
        meta: { sortKey: 'policyBand', mobileLabel: 'Band', hideOnMobile: true },
        cell: ({ row }) => (
          <StackedCell
            primary={describeBand(row.original)}
            secondary={describeWindow(row.original)}
          />
        ),
      },
      {
        id: 'status',
        header: 'Status',
        meta: { sortKey: 'status', mobileLabel: 'Status' },
        cell: ({ row }) => (
          <StackedCell
            primary={<StatusBadge status={row.original.status} />}
            secondary={describeStatus(row.original)}
          />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        meta: { align: 'right', sticky: true, width: '210px' },
        cell: ({ row }) => (
          <RowActions>
            <Button size="sm" variant="secondary" onClick={() => setSelected(row.original)}>
              Details
            </Button>
            {/* `waivable` is the server's judgement: a settled penalty has
                already been paid, and forgiving it would create a credit
                nobody issued. */}
            {row.original.waivable ? (
              <Button
                size="sm"
                variant="secondary"
                iconLeft={<HandCoins aria-hidden="true" />}
                onClick={() => setWaiving(row.original)}
              >
                Waive
              </Button>
            ) : null}
          </RowActions>
        ),
      },
    ],
    [],
  )

  const exportColumns = useMemo<ExportColumn<CustomerPenalty>[]>(
    () => [
      { header: 'ID', value: (row) => row.id },
      { header: 'Customer', value: (row) => row.customerName ?? '' },
      { header: 'Customer phone', value: (row) => row.customerPhone ?? '' },
      // Major units, so a finance spreadsheet can sum the column directly.
      { header: 'Amount', value: (row) => (row.amountMinor / 100).toFixed(2) },
      { header: 'Booking price', value: (row) => (row.bookingPriceMinor / 100).toFixed(2) },
      { header: 'Currency', value: (row) => row.currency },
      { header: 'Status', value: (row) => row.status ?? '' },
      { header: 'From booking', value: (row) => row.sourceBookingReference ?? '' },
      { header: 'Service', value: (row) => row.serviceType ?? '' },
      { header: 'Band', value: (row) => row.policyBand ?? '' },
      { header: 'Refund percent', value: (row) => row.refundPercent },
      { header: 'Minutes before pickup', value: (row) => row.minutesBeforePickup ?? '' },
      { header: 'Scheduled pickup', value: (row) => formatDateTimeCompact(row.scheduledPickupAt, '') },
      { header: 'Charged', value: (row) => formatDateTimeCompact(row.createdAt, '') },
      { header: 'Reason', value: (row) => row.reason ?? '' },
      { header: 'Charged by', value: (row) => row.createdBySource ?? '' },
      { header: 'Settled by booking', value: (row) => row.settledBookingReference ?? '' },
      { header: 'Settled', value: (row) => formatDateTimeCompact(row.settledAt, '') },
      { header: 'Waived by', value: (row) => row.waivedByAdminName ?? '' },
      { header: 'Waive reason', value: (row) => row.waiveReason ?? '' },
      { header: 'Waived', value: (row) => formatDateTimeCompact(row.waivedAt, '') },
    ],
    [],
  )

  const page = penaltiesQuery.data
  const outstandingCount = page?.content.filter((penalty) => penalty.outstanding).length ?? 0

  return (
    <>
      <PageHeader
        title="Cancellation penalties"
        description="What customers owe for cancelling a cash booking too late. A penalty is collected on the customer's next booking, which must be paid by card, and is never discounted by a promo code."
        meta={
          page ? (
            <>
              <span>{page.totalElements} penalties</span>
              {outstandingCount > 0 ? <span>{outstandingCount} outstanding on this page</span> : null}
            </>
          ) : null
        }
      />

      <Card flush>
        <FilterBar
          search={
            <SearchInput
              value={table.filters.search}
              onChange={(value) => table.setFilter('search', value)}
              placeholder="Search booking reference, reason or customer id…"
              label="Search penalties"
            />
          }
          actions={
            <ExportMenu
              columns={exportColumns}
              baseName="drive-for-me-penalties"
              title="Drive For Me — Cancellation penalties"
              fetchRows={() =>
                fetchAllPages((exportPage, exportSize) =>
                  fetchPenalties({ ...filters, page: exportPage, size: exportSize }),
                )
              }
            />
          }
          activeFilterCount={table.activeFilterCount}
          onReset={table.resetFilters}
        >
          <FilterSelect
            label="Status"
            value={table.filters.status}
            onChange={(value) => table.setFilter('status', value)}
            options={PENALTY_STATUSES.map((status) => ({ value: status, label: humanizeEnum(status) }))}
          />
          <FilterSelect
            label="Charged by"
            value={table.filters.source}
            onChange={(value) => table.setFilter('source', value)}
            options={PENALTY_SOURCES.map((source) => ({ value: source, label: humanizeEnum(source) }))}
          />
        </FilterBar>

        <DataTable
          caption="Drive For Me cancellation penalties"
          data={page?.content ?? []}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={penaltiesQuery.isLoading}
          isFetching={penaltiesQuery.isFetching && !penaltiesQuery.isLoading}
          error={penaltiesQuery.isError ? penaltiesQuery.error : undefined}
          onRetry={() => void penaltiesQuery.refetch()}
          sort={table.sort}
          direction={table.direction}
          onSortChange={table.setSort}
          emptyTitle={
            table.activeFilterCount > 0 ? 'No penalties match these filters' : 'No cancellation penalties'
          }
          emptyDescription={
            table.activeFilterCount > 0
              ? 'Try a different status, or clear the filters.'
              : 'A penalty appears here when a cash booking is cancelled too close to its pickup time.'
          }
          emptyAction={
            table.activeFilterCount > 0 ? (
              <Button variant="secondary" onClick={table.resetFilters}>
                Clear filters
              </Button>
            ) : null
          }
        />

        {page && page.totalElements > 0 ? (
          <Pagination
            page={page.page}
            size={page.size}
            totalElements={page.totalElements}
            totalPages={page.totalPages}
            onPageChange={table.setPage}
            onSizeChange={table.setSize}
            noun="penalty"
            pluralNoun="penalties"
            disabled={penaltiesQuery.isFetching}
          />
        ) : null}
      </Card>

      <PenaltyDetailsDrawer
        penalty={selected}
        onClose={() => setSelected(null)}
        onWaive={(penalty) => {
          setSelected(null)
          setWaiving(penalty)
        }}
      />

      <WaivePenaltyDialog penalty={waiving} onClose={() => setWaiving(null)} />
    </>
  )
}
